import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Calendar,
  Download,
  FileText,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import editaisPublicosService from "../../../services/editaisPublicosService";
import type { EditalPublicoDetalhado } from "../../../services/editaisPublicosService";

export default function DetalhesEditalPublico() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [edital, setEdital] = useState<EditalPublicoDetalhado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const carregarDados = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        const editalResponse = await editaisPublicosService.buscar(parseInt(id));
        setEdital(editalResponse.edital);
      } catch (err) {
        setError("Erro ao carregar edital");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    void carregarDados();
  }, [id]);

  const handleInscrever = () => {
    if (!id) return;
    navigate(`/inscricoes/nova?edital_id=${id}`);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando edital...</p>
        </div>
      </div>
    );
  }

  if (error || !edital) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || "Edital não encontrado"}</p>
        </div>
        <Link
          to="/editais"
          className="mt-4 inline-block text-blue-600 hover:text-blue-800"
        >
          ← Voltar para lista
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto p-6">
        {/* Botão Voltar */}
        <button
          onClick={() => navigate(-1)}
          className="mb-6 text-gray-600 hover:text-gray-900 font-medium flex items-center gap-2 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Voltar
        </button>

        {/* Cabeçalho */}
        <div className="border-b-2 border-gray-300 pb-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {edital.nome}
              </h1>
              <div className="text-base text-gray-700 leading-relaxed whitespace-pre-line mt-8">
                {edital.descricao}
              </div>
            </div>
            {edital.status_periodo && (
              <span
                className={`px-4 py-2 rounded text-sm font-semibold whitespace-nowrap border ${
                  edital.status_periodo === "aberto"
                    ? "bg-green-100 text-green-800 border-green-300"
                    : edital.status_periodo === "em_breve"
                      ? "bg-blue-100 text-blue-800 border-blue-300"
                      : "bg-gray-100 text-gray-800 border-gray-300"
                }`}
              >
                {edital.status_periodo === "aberto"
                  ? "Aberto"
                  : edital.status_periodo === "em_breve"
                    ? "Em Breve"
                    : "Encerrado"}
              </span>
            )}
          </div>
        </div>

        {/* Informações Principais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Período de Submissão */}
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded border border-gray-200">
            <Calendar className="w-5 h-5 text-gray-700 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-gray-900 mb-1">
                Período de Inscrição
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Abertura:</span>{" "}
                {new Date(edital.data_inicio).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-medium">Encerramento:</span>{" "}
                {new Date(edital.data_fim).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>

          {/* Prazo Restante / Dias para Início */}
          {edital.status_periodo === "aberto" && edital.dias_restantes !== undefined && (
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded border border-gray-200">
              <AlertCircle
                className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  edital.dias_restantes <= 7
                    ? "text-red-600"
                    : edital.dias_restantes <= 14
                    ? "text-yellow-600"
                    : "text-gray-700"
                }`}
              />
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Prazo Restante
                </p>
                <p
                  className={`text-sm font-medium ${
                    edital.dias_restantes <= 7
                      ? "text-red-700"
                      : edital.dias_restantes <= 14
                      ? "text-yellow-700"
                      : "text-gray-700"
                  }`}
                >
                  {edital.dias_restantes === 0
                    ? "Último dia para inscrição"
                    : edital.dias_restantes === 1
                    ? "1 dia para inscrição"
                    : `${edital.dias_restantes} dias para inscrição`}
                </p>
              </div>
            </div>
          )}

          {edital.status_periodo === "em_breve" && edital.dias_para_inicio !== undefined && (
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded border border-blue-200">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Inscrições em breve
                </p>
                <p className="text-sm font-medium text-blue-700">
                  {edital.dias_para_inicio === 0
                    ? "Abre hoje"
                    : edital.dias_para_inicio === 1
                    ? "Abre amanhã"
                    : `Abre em ${edital.dias_para_inicio} dias`}
                </p>
              </div>
            </div>
          )}

          {/* Formação Mínima Requerida */}
          {edital.formacao_minima_requerida && (
            <div className="flex items-start space-x-3 p-4 bg-purple-50 rounded border border-purple-200">
              <FileText className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Formação Acadêmica Mínima
                </p>
                <p className="text-sm text-purple-700 font-medium capitalize">
                  {edital.formacao_minima_requerida
                    .replace("_", "-")
                    .replace("pos_doutorado", "Pós-Doutorado")
                    .replace("pos-doutorado", "Pós-Doutorado")
                    .replace("doutorado", "Doutorado")
                    .replace("mestrado", "Mestrado")
                    .replace("especializacao", "Especialização")
                    .replace("graduacao", "Graduação")}
                </p>
              </div>
            </div>
          )}

          {/* Status de Elegibilidade - Baseado apenas em requisitos */}
          {edital.elegivel !== undefined && !edital.ja_inscrito && (
            <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded border border-gray-200">
              {edital.elegivel ? (
                <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" />
              )}
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Status de Elegibilidade
                </p>
                <p
                  className={`text-sm font-medium ${
                    edital.elegivel ? "text-green-700" : "text-amber-700"
                  }`}
                >
                  {edital.elegivel
                    ? "Você atende aos requisitos do edital"
                    : edital.motivo_inelegibilidade || "Verifique os requisitos"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Documentos e Anexos do Edital */}
        {edital.anexos && edital.anexos.length > 0 && (
          <div className="mb-8">
            <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
              <Download className="w-5 h-5 mr-2" />
              Documentos e Anexos
            </h3>
            <div className="space-y-2">
              {edital.anexos.map((anexo) => (
                <a
                  key={anexo.id}
                  href={anexo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-between p-4 bg-white border border-gray-300 rounded hover:bg-gray-50 hover:border-gray-400 transition-colors"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <FileText className="w-5 h-5 text-gray-600 flex-shrink-0" />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-gray-900 block">
                        {anexo.filename}
                      </span>
                      <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                        <span>{(anexo.byte_size / 1024 / 1024).toFixed(2)} MB</span>
                        <span>•</span>
                        <span>
                          Adicionado em{" "}
                          {new Date(anexo.created_at).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Download className="w-4 h-4 text-gray-600 flex-shrink-0 ml-3" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Botão de Ação */}
        <div className="border-t border-gray-300 pt-6">
          {edital.ja_inscrito ? (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <p className="text-green-900 font-bold text-lg mb-2">
                Inscrição Realizada
              </p>
              <p className="text-green-700 mb-4">
                Você já possui uma inscrição neste edital
              </p>
              <Link
                to="/inscricoes"
                className="inline-block bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 transition font-medium"
              >
                Ver Minhas Inscrições
              </Link>
            </div>
          ) : edital.pode_inscrever ? (
            <>
              <button
                onClick={handleInscrever}
                className="w-full bg-gray-900 text-white py-3 px-6 rounded font-semibold hover:bg-gray-800 transition-colors"
              >
                Submeter Proposta
              </button>
              <p className="text-xs text-gray-600 text-center mt-3">
                Certifique-se de ler todos os documentos antes de submeter sua proposta
              </p>
            </>
          ) : (
            <div className={`rounded p-4 ${
              edital.motivo_nao_pode_inscrever?.includes('abrem em') 
                ? 'bg-blue-50 border border-blue-200' 
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                  edital.motivo_nao_pode_inscrever?.includes('abrem em')
                    ? 'text-blue-600'
                    : 'text-amber-600'
                }`} />
                <div>
                  <p className={`font-medium mb-1 ${
                    edital.motivo_nao_pode_inscrever?.includes('abrem em')
                      ? 'text-blue-800'
                      : 'text-amber-800'
                  }`}>
                    {edital.motivo_nao_pode_inscrever?.includes('abrem em')
                      ? 'Inscrições em breve'
                      : 'Inscrição não disponível'}
                  </p>
                  <p className={`text-sm ${
                    edital.motivo_nao_pode_inscrever?.includes('abrem em')
                      ? 'text-blue-700'
                      : 'text-amber-700'
                  }`}>
                    {edital.motivo_nao_pode_inscrever || "Verifique os requisitos do edital"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
